import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Search, Clock, Zap, CheckCircle, ChevronRight, Building2, Calendar, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ticketService } from '../services/ticketService';
import Sidebar from '../components/Sidebar';

const TicketList = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (currentUser && userData) {
      const unsubscribe = ticketService.subscribeToTickets(
        currentUser.uid,
        userData.role,
        (tickets) => {
          setTickets(tickets);
          setLoading(false);
        }
      );

      return unsubscribe;
    }
  }, [currentUser, userData]);

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.propertyName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'in-progress': return 'bg-slate-100 text-slate-800 border-slate-300';
      case 'completed': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'closed': return 'bg-slate-50 text-slate-500 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'medium': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'low': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-600 mx-auto" />
          <p className="mt-4 text-sm font-medium text-slate-600">Loading tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10 w-full">
            <div className="flex items-center justify-between px-8 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-slate-800">{t('dashboard.owner.pages.tickets.title')}</h1>
                  <p className="text-slate-500 text-sm">Maintenance requests and work orders</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm text-slate-500 pl-3 border-l border-slate-200">
                  {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                </div>
                <span className="px-2.5 py-1 bg-slate-100 rounded-md text-xs font-medium text-slate-700 capitalize">{userData?.role}</span>
              </div>
            </div>
          </header>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{t('dashboard.owner.pages.tickets.totalTickets')}</p>
                    <p className="text-2xl font-semibold text-slate-800 mt-1 tabular-nums">{tickets.length}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Open</p>
                    <p className="text-2xl font-semibold text-slate-800 mt-1 tabular-nums">{tickets.filter(t => t.status === 'open').length}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">In Progress</p>
                    <p className="text-2xl font-semibold text-slate-800 mt-1 tabular-nums">{tickets.filter(t => t.status === 'in-progress').length}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Completed</p>
                    <p className="text-2xl font-semibold text-slate-800 mt-1 tabular-nums">{tickets.filter(t => t.status === 'completed').length}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.75} />
                  <input
                    type="text"
                    placeholder="Search by title, description, or property..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                  />
                </div>
                <div className="md:w-44">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                  >
                    <option value="all">All Status</option>
                    <option value="open">Open</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-semibold text-slate-800">{t('dashboard.owner.pages.tickets.yourTickets')}</h2>
                <span className="text-sm text-slate-500">{filteredTickets.length} {filteredTickets.length === 1 ? t('dashboard.owner.pages.tickets.ticket') : t('dashboard.owner.pages.tickets.tickets')}</span>
              </div>

              {filteredTickets.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <ClipboardList className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-semibold text-slate-800 mb-2">No tickets found</h3>
                  <p className="text-sm text-slate-500">
                    {searchTerm || statusFilter !== 'all' ? 'Try adjusting your search or filter.' : 'No maintenance tickets have been created yet.'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filteredTickets.map((ticket) => (
                    <Link
                      key={ticket.id}
                      to={`/tickets/${ticket.id}`}
                      className="bg-white rounded-lg border border-slate-200 p-3 hover:border-slate-300 hover:shadow-sm transition-all flex flex-col min-h-0"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex flex-wrap gap-1 min-w-0">
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border shrink-0 ${getStatusColor(ticket.status)}`}>{ticket.status}</span>
                          {ticket.priority && (
                            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border shrink-0 ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</span>
                          )}
                        </div>
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center shrink-0">
                          <ClipboardList className="w-3 h-3 text-slate-600" strokeWidth={1.75} />
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-slate-800 hover:text-slate-600 line-clamp-1 mb-1">{ticket.title || 'Untitled Ticket'}</h3>
                      <p className="text-xs text-slate-500 line-clamp-2 mb-2 flex-1 min-h-0">
                        {ticket.description?.substring(0, 80)}
                        {(ticket.description?.length || 0) > 80 && '...'}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-wrap">
                        <span className="inline-flex items-center gap-1 truncate max-w-full">
                          <Building2 className="w-3 h-3 shrink-0" strokeWidth={1.75} />
                          <span className="truncate">{ticket.propertyName || '—'}</span>
                        </span>
                        <span className="shrink-0">·</span>
                        <span className="inline-flex items-center gap-1 shrink-0">
                          <Calendar className="w-3 h-3" strokeWidth={1.75} />
                          {ticket.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </span>
                      </div>
                      {ticket.tenantName && (
                        <p className="text-[10px] text-slate-400 truncate mt-1 flex items-center gap-1">
                          <User className="w-2.5 h-2.5 shrink-0" strokeWidth={1.75} />
                          {ticket.tenantName}
                        </p>
                      )}
                      <span className="mt-2 inline-flex items-center justify-center gap-1 text-xs font-medium text-slate-600 group-hover:text-slate-800">
                        View
                        <ChevronRight className="w-3 h-3" strokeWidth={1.75} />
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketList; 